import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Stack, Typography, IconButton, Tooltip, Paper, Chip, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, InputAdornment, TextField,
  TableSortLabel, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { BRAND } from "../styles/theme";
import { listDebines, syncDebines, confirmDebin, rejectDebin, getDebinComprobanteUrl, type Debin, type Ambiente } from "../services/debines";


const ESTADO_META: Record<string, { label: string; fg: string; bg: string }> = {
  INICIADO:           { label: "Iniciado",           fg: "#9A3412", bg: "#FFEDD5" },
  ACREDITADO:         { label: "Acreditado",         fg: "#166534", bg: "#DCFCE7" },
  "RECHAZO DE CLIENTE": { label: "Rechazado",        fg: "#991B1B", bg: "#FEE2E2" },
  VENCIDO:            { label: "Vencido",            fg: "#374151", bg: "#E5E7EB" },
  "ERROR DATOS":      { label: "Error datos",        fg: "#7C3AED", bg: "#EDE9FE" },
  "ERROR DEBITO":     { label: "Error débito",       fg: "#92400E", bg: "#FEF3C7" },
  "SIN GARANTIA":     { label: "Sin garantía",       fg: "#7F1D1D", bg: "#FECACA" },
  _PROCESANDO:        { label: "Procesando…",        fg: "#1D4ED8", bg: "#DBEAFE" },
};

function fmtMoney(v: number): string {
  return v.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Parsea un importe pegado en formato en-US (`-13,534,176.00`) o es-AR
 *  (`-13.534.176,00`); el signo se ignora. Un separador seguido de 3 dígitos
 *  al final cuenta como miles (los importes tienen a lo sumo 2 decimales), así
 *  que no hay ambigüedad entre ambos formatos. Devuelve el valor ABSOLUTO o
 *  null si el texto no tiene ninguna de las dos formas. */
function parsePastedImporte(raw: string): number | null {
  const s = raw.trim().replace(/\s+/g, "");
  // en-US: coma = miles, punto = decimales
  if (/^-?\d{1,3}(,\d{3})*(\.\d{1,2})?$/.test(s) || /^-?\d+(\.\d{1,2})?$/.test(s)) {
    const n = parseFloat(s.replace(/^-/, "").replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  // es-AR: punto = miles, coma = decimales
  if (/^-?\d{1,3}(\.\d{3})*(,\d{1,2})?$/.test(s) || /^-?\d+(,\d{1,2})?$/.test(s)) {
    const n = parseFloat(s.replace(/^-/, "").replace(/\./g, "").replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function fmtDateTime(iso: string): { date: string; time: string } {
  if (!iso) return { date: "—", time: "" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: iso, time: "" };
  const date = d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  return { date, time };
}

function EstadoChip({ estado }: { estado: string }) {
  const meta = ESTADO_META[estado] ?? { label: estado || "—", fg: "#374151", bg: "#E5E7EB" };
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{ fontWeight: 700, fontSize: 11, height: 24, borderRadius: 3, color: meta.fg, bgcolor: meta.bg }}
    />
  );
}

const ALL_ESTADOS = Object.keys(ESTADO_META);

type SortKey = "id" | "inicio" | "vencimiento" | "originante" | "destino" | "moneda" | "importe" | "estado";

// Devuelve el valor comparable de un DEBIN para una columna dada.
function sortValue(d: Debin, key: SortKey): string | number {
  switch (key) {
    case "id":          return d.id.toLowerCase();
    case "inicio":      { const t = new Date(d.fecha).getTime(); return isNaN(t) ? 0 : t; }
    case "vencimiento": { const t = new Date(d.vencimiento).getTime(); return isNaN(t) ? 0 : t; }
    case "originante":  return (d.razon_social || "").toLowerCase();
    case "destino":     return (d.collector || d.cbu_destino || "").toLowerCase();
    case "moneda":      return (d.moneda || "").toLowerCase();
    case "importe":     return d.importe;
    case "estado":      return (d.estado || "").toLowerCase();
  }
}

// En producción solo se operan DEBINes cuyo CBU originante sea el esperado.
// Cualquier otro se marca para revisión manual (solo lectura).
// Guard deshabilitado temporalmente para operar DEBINes de otros originantes.
const ORIGINANTE_GUARD_ENABLED = false;
const ALLOWED_ORIGINANTE_CBU_PRD = "0168888100000606620295";

const LS_KEY = "webadmin2.debin.confirmed";
const ONE_HOUR_MS = 3_600_000;

function lsRead(): Record<string, { estado: string; ts: number }> {
  try { return JSON.parse(localStorage.getItem(LS_KEY) ?? "{}"); }
  catch { return {}; }
}
function lsSet(id: string, estado: string) {
  const data = lsRead();
  data[id] = { estado, ts: Date.now() };
  localStorage.setItem(LS_KEY, JSON.stringify(data));
}
function lsDel(id: string) {
  const data = lsRead();
  delete data[id];
  localStorage.setItem(LS_KEY, JSON.stringify(data));
}

export default function Settlements() {
  // Ambiente Coelsa del deployment (lo informa el backend; sandbox → homo, prd → prd).
  const [ambiente,       setAmbiente]       = useState<Ambiente | null>(null);
  const [debins,         setDebins]         = useState<Debin[]>([]);
  const [loading,        setLoading]        = useState(false);
  const [estadoFilter,   setEstadoFilter]   = useState<string | null>("INICIADO");
  const [search,         setSearch]         = useState("");
  const [flashMsg,       setFlashMsg]       = useState<{ kind: "success" | "info" | "warning" | "error"; text: string } | null>(null);
  const [pendingIds,     setPendingIds]     = useState<Set<string>>(new Set());
  const [compBusy,       setCompBusy]       = useState<Set<string>>(new Set());
  // Orden de la tabla. Default: vencimiento descendente (más nuevos primero).
  const [sortKey,        setSortKey]        = useState<SortKey>("vencimiento");
  const [sortDir,        setSortDir]        = useState<"asc" | "desc">("desc");
  // Hora del último sync Coelsa→cache (el backend refresca cada 5 min).
  const [syncedAt,       setSyncedAt]       = useState<string | null>(null);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function notify(kind: "success" | "info" | "warning" | "error", text: string) {
    setFlashMsg({ kind, text });
  }

  // silent=true: refresca datos SIN tocar `loading` (ni spinner ni flicker) —
  // para el refresh de fondo post-acciones, que no debe interrumpir al operador.
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await listDebines();
      setSyncedAt(data.synced_at ?? null);
      if (data.ambiente) setAmbiente(data.ambiente);
      const overrides = lsRead();
      const merged = data.items.map((item) => {
        const entry = overrides[item.id];
        if (entry) {
          const expired = Date.now() - entry.ts > ONE_HOUR_MS;
          if (!expired && item.estado === "INICIADO") return { ...item, estado: entry.estado };
          lsDel(item.id); // Coelsa ya actualizó, o expiró — limpiamos
        }
        return item;
      });
      setDebins(merged);
    } catch (e) {
      if (!silent) notify("error", `Error al cargar DEBINes: ${String(e)}`);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh silencioso DEBOUNCED tras aprobar/rechazar: un solo timer que cada
  // acción re-programa a +2 min del ÚLTIMO click. Así se pueden ejecutar varios
  // DEBINes en una misma "ventana de atención" sin recargas en el medio (la UI
  // queda optimista vía lsSet; el sync de Coelsa después confirma el estado real).
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  function scheduleSilentRefresh() {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      refreshTimer.current = null;
      load(true);
    }, 120_000);
  }
  useEffect(() => () => {
    // limpiar al desmontar (el load capturado sería viejo)
    if (refreshTimer.current) { clearTimeout(refreshTimer.current); refreshTimer.current = null; }
  }, []);

  // Actualización A DEMANDA: dispara el sync contra Coelsa y espera a que el
  // cache cambie (synced_at) antes de recargar. El rate de 5 min sigue corriendo
  // de fondo; esto es para no tener que esperarlo.
  const [syncing, setSyncing] = useState(false);
  async function onRefresh() {
    setSyncing(true);
    try {
      const { prev_synced_at } = await syncDebines();
      const deadline = Date.now() + 75_000;
      let changed = false;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const data = await listDebines();
        if ((data.synced_at ?? "") !== prev_synced_at) { changed = true; break; }
      }
      await load();
      if (changed) notify("success", "Listado sincronizado con Coelsa.");
      else notify("warning", "Coelsa está tardando más de lo esperado; el listado se va a actualizar solo en breve.");
    } catch (e) {
      notify("error", `No se pudo sincronizar: ${String(e)}`);
      await load();
    } finally {
      setSyncing(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = debins.filter((d) => {
      if (pendingIds.has(d.id)) return true;  // siempre visible mientras se procesa
      if (estadoFilter && d.estado !== estadoFilter) return false;
      if (!q) return true;
      return (
        d.id.toLowerCase().includes(q) ||
        d.razon_social.toLowerCase().includes(q) ||
        d.cuit_originante.toLowerCase().includes(q) ||
        d.cbu_originante.toLowerCase().includes(q) ||
        d.cbu_destino.toLowerCase().includes(q) ||
        d.collector.toLowerCase().includes(q)
      );
    });
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = sortValue(a, sortKey), vb = sortValue(b, sortKey);
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
  }, [debins, estadoFilter, search, pendingIds, sortKey, sortDir]);

  const stats = useMemo(() => {
    let iniciados = 0, acreditados = 0, rechazados = 0, totalIniciado = 0;
    for (const d of debins) {
      if      (d.estado === "INICIADO")           { iniciados++;   totalIniciado += d.importe; }
      else if (d.estado === "ACREDITADO")           acreditados++;
      else if (d.estado === "RECHAZO DE CLIENTE")   rechazados++;
    }
    return { total: debins.length, iniciados, acreditados, rechazados, totalIniciado };
  }, [debins]);

  function markProcesando(id: string, nuevoEstado: string) {
    setDebins((prev) => prev.map((x) => x.id === id ? { ...x, estado: nuevoEstado } : x));
  }

  function setPending(id: string, on: boolean) {
    setPendingIds((prev) => {
      const next = new Set(prev);
      on ? next.add(id) : next.delete(id);
      return next;
    });
  }

  // Confirmación en dos pasos: el click abre un diálogo donde hay que PEGAR el
  // importe (formato -13,534,176.00, el signo no importa); solo si coincide en
  // valor absoluto con el del DEBIN se dispara la aprobación real.
  const [confirmTarget, setConfirmTarget] = useState<Debin | null>(null);
  const [confirmValue,  setConfirmValue]  = useState("");
  const [confirmErr,    setConfirmErr]    = useState<string | null>(null);

  function openConfirm(d: Debin) {
    setConfirmTarget(d); setConfirmValue(""); setConfirmErr(null);
  }

  function onDialogApprove() {
    if (!confirmTarget) return;
    const parsed = parsePastedImporte(confirmValue);
    if (parsed == null) {
      setConfirmErr("Formato inválido. Pegá el importe como -13,534,176.00 o 13.534.176,00.");
      return;
    }
    const expected = Math.abs(confirmTarget.importe);
    if (Math.abs(parsed - expected) >= 0.005) {
      setConfirmErr(`El importe pegado (${parsed.toLocaleString("en-US", { minimumFractionDigits: 2 })}) `
        + `NO coincide con el del DEBIN (${Math.abs(confirmTarget.importe).toLocaleString("en-US", { minimumFractionDigits: 2 })}). No se aprobó.`);
      return;
    }
    const d = confirmTarget;
    setConfirmTarget(null);
    onConfirm(d);
  }

  async function onConfirm(d: Debin) {
    setPending(d.id, true);
    markProcesando(d.id, "_PROCESANDO");
    try {
      await confirmDebin(d.id, d.importe, d.cbu_destino, d.cuit_comprador, d.cuit_originante, d.cbu_originante);
      // Optimista = "Procesando…", NO "Acreditado": Coelsa solo aceptó el débito
      // ("se da curso al crédito"), la acreditación real de fondos ocurre después y
      // la refleja el sync (o queda en INICIADO/ERROR si no acredita). Mostrar
      // "Acreditado" acá era prematuro (y habilitaba el botón de comprobante, que
      // el backend rechaza con 409 hasta que ESTÁ acreditado de verdad).
      lsSet(d.id, "_PROCESANDO");
      markProcesando(d.id, "_PROCESANDO");
      notify("success", `DEBIN ${d.id} confirmado por ARS ${fmtMoney(d.importe)} (débito aceptado; acreditación en curso).`);
    } catch (e) {
      markProcesando(d.id, "INICIADO");
      notify("error", `Error al confirmar ${d.id}: ${String(e)}`);
    } finally {
      // Sin reload acá: la fila ya quedó optimista (lsSet) y un load() ahora
      // interrumpiría al operador que está ejecutando varios DEBINes seguidos.
      setPending(d.id, false);
      scheduleSilentRefresh();
    }
  }

  async function onReject(d: Debin) {
    setPending(d.id, true);
    markProcesando(d.id, "_PROCESANDO");
    try {
      await rejectDebin(d.id, d.importe, d.cbu_destino, d.cuit_comprador, d.cuit_originante, d.cbu_originante);
      lsSet(d.id, "RECHAZO DE CLIENTE");
      markProcesando(d.id, "RECHAZO DE CLIENTE");
      notify("info", `DEBIN ${d.id} rechazado.`);
    } catch (e) {
      markProcesando(d.id, "INICIADO");
      notify("error", `Error al rechazar ${d.id}: ${String(e)}`);
    } finally {
      setPending(d.id, false);
      scheduleSilentRefresh();
    }
  }

  function setComp(id: string, on: boolean) {
    setCompBusy((prev) => {
      const next = new Set(prev);
      on ? next.add(id) : next.delete(id);
      return next;
    });
  }

  async function onDownloadComprobante(d: Debin) {
    setComp(d.id, true);
    try {
      const url = await getDebinComprobanteUrl(d);
      const a = document.createElement("a");
      a.href = url;
      a.download = `comprobante_debin_${d.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      notify("error", `No se pudo generar el comprobante de ${d.id}: ${String(e)}`);
    } finally {
      setComp(d.id, false);
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>DEBINes</Typography>
        <Tooltip title="Solicitudes de DEBIN recibidas. Confirmá o rechazá los que están en estado Iniciado.">
          <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        </Tooltip>

        <Box sx={{ ml: "auto" }}>
          {ambiente && (
            <Tooltip title="Ambiente Coelsa de este deployment: lo fija la infraestructura (sandbox → homologación, producción → producción), no se elige acá.">
              <Chip
                label={ambiente === "homo" ? "Homologación" : "Producción"}
                size="small"
                sx={{
                  fontWeight: 700, fontSize: 12, height: 28, borderRadius: 2,
                  bgcolor: ambiente === "prd" ? BRAND.primary : "#FEF3C7",
                  color:   ambiente === "prd" ? "#fff" : "#92400E",
                }}
              />
            </Tooltip>
          )}
        </Box>
      </Stack>

      {flashMsg && (
        <Alert severity={flashMsg.kind} sx={{ mb: 2 }} onClose={() => setFlashMsg(null)}>
          {flashMsg.text}
        </Alert>
      )}

      {/* Summary panel */}
      <Box sx={{ bgcolor: BRAND.primaryLight, borderRadius: 2, px: 3, py: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems="stretch">
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1, mb: 0.5 }}>
              DEBINes
            </Typography>
            <Stack direction="row" spacing={2} alignItems="baseline">
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: BRAND.primary }}>{stats.total}</Typography>
                <Typography variant="caption" color="text.secondary">total</Typography>
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#9A3412" }}>{stats.iniciados}</Typography>
                <Typography variant="caption" sx={{ color: "#9A3412" }}>iniciados</Typography>
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#166534" }}>{stats.acreditados}</Typography>
                <Typography variant="caption" sx={{ color: "#166534" }}>acreditados</Typography>
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#991B1B" }}>{stats.rechazados}</Typography>
                <Typography variant="caption" sx={{ color: "#991B1B" }}>rechazados</Typography>
              </Box>
            </Stack>
          </Box>
          <Box sx={{ flex: 1, textAlign: "right" }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1, mb: 0.5 }}>
              Pendiente a confirmar
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: BRAND.primary }}>
              ARS {fmtMoney(stats.totalIniciado)}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Search + refresh */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Buscar por ID, razón social, CUIT o CBU…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 480, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {syncing && (
          <Typography variant="caption" sx={{ color: "#9A6700", whiteSpace: "nowrap", fontWeight: 600 }}>
            Sincronizando con Coelsa…
          </Typography>
        )}
        {syncedAt && !syncing && (
          <Tooltip title="Última sincronización con Coelsa (automática cada 5 min, o a demanda con el botón)">
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              Coelsa {new Date(syncedAt).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
            </Typography>
          </Tooltip>
        )}
        <Tooltip title="Actualizar AHORA desde Coelsa (tarda ~15-30 s)">
          <span>
            <IconButton
              onClick={onRefresh}
              disabled={loading || syncing}
              sx={{
                color: "#fff",
                bgcolor: BRAND.primary,
                borderRadius: 2,
                px: 1.5,
                py: 0.75,
                gap: 0.5,
                fontSize: 13,
                fontWeight: 600,
                "&:hover": { bgcolor: BRAND.primaryDark },
                "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
              }}
            >
              {loading || syncing ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* Estado filter chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", gap: 0.5 }}>
        <Chip
          label="Todos"
          size="small"
          onClick={() => setEstadoFilter(null)}
          sx={{
            fontWeight: 700, fontSize: 12, height: 26, borderRadius: 2, cursor: "pointer",
            bgcolor: estadoFilter === null ? BRAND.primary : "rgba(0,0,0,0.06)",
            color:   estadoFilter === null ? "#fff"        : "#555",
            "&:hover": { bgcolor: estadoFilter === null ? BRAND.primaryDark : "rgba(0,0,0,0.10)" },
          }}
        />
        {ALL_ESTADOS.map((e) => {
          const meta    = ESTADO_META[e];
          const active  = estadoFilter === e;
          return (
            <Chip
              key={e}
              label={meta.label}
              size="small"
              onClick={() => setEstadoFilter(active ? null : e)}
              sx={{
                fontWeight: 700, fontSize: 12, height: 26, borderRadius: 2, cursor: "pointer",
                bgcolor: active ? meta.fg  : meta.bg,
                color:   active ? "#fff"   : meta.fg,
                "&:hover": { opacity: 0.85 },
              }}
            />
          );
        })}
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: "auto" }}>
        <Table sx={{ minWidth: 1100 }}>
          <TableHead sx={{ bgcolor: BRAND.primaryLight }}>
            <TableRow>
              {([
                ["id",          "ID DEBIN",    "left"],
                ["inicio",      "Iniciado",    "left"],
                ["vencimiento", "Vencimiento", "left"],
                ["originante",  "Originante",  "left"],
                ["destino",     "CVU destino", "left"],
                ["moneda",      "Moneda",      "left"],
                ["importe",     "Importe",     "right"],
                ["estado",      "Estado",      "left"],
              ] as [SortKey, string, "left" | "right"][]).map(([key, label, align]) => (
                <TableCell key={key} sx={thSx} align={align} sortDirection={sortKey === key ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === key}
                    direction={sortKey === key ? sortDir : "asc"}
                    onClick={() => toggleSort(key)}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell sx={thSx} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.map((d) => {
              const v          = fmtDateTime(d.vencimiento);
              const ini        = fmtDateTime(d.fecha);
              const isIniciado = d.estado === "INICIADO";
              const isPending  = pendingIds.has(d.id);
              // En prd, originante no reconocido → solo lectura (se marca con un ojo).
              const flagged    = ORIGINANTE_GUARD_ENABLED && ambiente === "prd" && d.cbu_originante !== ALLOWED_ORIGINANTE_CBU_PRD;
              const canAct     = isIniciado && !isPending && !flagged;
              return (
                <TableRow key={d.id} hover>
                  <TableCell>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                      {d.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{ini.date || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary">{ini.time}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{v.date}</Typography>
                    <Typography variant="caption" color="text.secondary">{v.time}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{d.razon_social || "—"}</Typography>
                      {flagged && (
                        <Tooltip title="Originante no reconocido — solo lectura. Revisar manualmente.">
                          <VisibilityOutlinedIcon sx={{ fontSize: 16, color: "#94A3B8", opacity: 0.8 }} />
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      CUIT {d.cuit_originante}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                      {d.cbu_originante || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{d.collector || "—"}</Typography>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                      {d.cbu_destino || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={d.moneda || "—"}
                      size="small"
                      sx={{ bgcolor: "#F4F2FF", color: BRAND.primary, fontWeight: 700, height: 22, borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "#222" }}>
                    {fmtMoney(d.importe)}
                  </TableCell>
                  <TableCell>
                    <EstadoChip estado={d.estado} />
                  </TableCell>
                  <TableCell align="center">
                    {d.estado === "ACREDITADO" ? (
                      <Tooltip title="Descargar comprobante PDF">
                        <span>
                          <IconButton
                            onClick={() => onDownloadComprobante(d)}
                            disabled={compBusy.has(d.id)}
                            sx={{ p: 1.25, color: "#166534", bgcolor: "#DCFCE7", "&:hover": { bgcolor: "#BBF7D0" }, "&.Mui-disabled": { color: "#CBD5E1" } }}
                          >
                            {compBusy.has(d.id)
                              ? <CircularProgress size={22} sx={{ color: "#94A3B8" }} />
                              : <PictureAsPdfIcon sx={{ fontSize: 24 }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : flagged ? (
                      <Tooltip title="Originante no reconocido — solo lectura. Revisar manualmente.">
                        <VisibilityOutlinedIcon sx={{ fontSize: 22, color: "#94A3B8", opacity: 0.8 }} />
                      </Tooltip>
                    ) : (
                      <Stack direction="row" spacing={2} justifyContent="center">
                        <Tooltip title={canAct ? "Confirmar DEBIN" : isPending ? "Procesando…" : "Solo disponible en estado Iniciado"}>
                          <span>
                            <IconButton
                              onClick={() => openConfirm(d)}
                              disabled={!canAct}
                              sx={{ p: 1.25, color: "#166534", bgcolor: canAct ? "#DCFCE7" : "transparent", "&:hover": { bgcolor: "#BBF7D0" }, "&.Mui-disabled": { color: "#CBD5E1" } }}
                            >
                              {isPending ? <CircularProgress size={22} sx={{ color: "#94A3B8" }} /> : <CheckCircleIcon sx={{ fontSize: 26 }} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={canAct ? "Rechazar DEBIN" : isPending ? "Procesando…" : "Solo disponible en estado Iniciado"}>
                          <span>
                            <IconButton
                              onClick={() => onReject(d)}
                              disabled={!canAct}
                              sx={{ p: 1.25, color: "#991B1B", bgcolor: canAct ? "#FEE2E2" : "transparent", "&:hover": { bgcolor: "#FECACA" }, "&.Mui-disabled": { color: "#CBD5E1" } }}
                            >
                              <CancelIcon sx={{ fontSize: 26 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && !filtered.length && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary" }}>
                  No hay DEBINes para mostrar.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!confirmTarget} onClose={() => setConfirmTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar DEBIN</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ mb: 2 }}>
            Vas a aprobar el DEBIN <b>{confirmTarget?.id}</b> de{" "}
            <b>{confirmTarget?.razon_social || confirmTarget?.collector}</b> por{" "}
            <b>ARS {confirmTarget ? fmtMoney(confirmTarget.importe) : ""}</b>.
            <br />Para confirmar, pegá el importe (formato <code>-13,534,176.00</code> o{" "}
            <code>13.534.176,00</code>; el signo no importa) — tiene que coincidir con el del DEBIN.
          </DialogContentText>
          <TextField autoFocus fullWidth size="small" label="Importe" value={confirmValue}
            onChange={(e) => { setConfirmValue(e.target.value); setConfirmErr(null); }}
            onKeyDown={(e) => { if (e.key === "Enter") onDialogApprove(); }}
            placeholder="-13,534,176.00"
            inputProps={{ style: { fontFamily: "monospace" } }} />
          {confirmErr && <Alert severity="error" sx={{ mt: 1.5 }}>{confirmErr}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTarget(null)}>Cancelar</Button>
          <Button onClick={onDialogApprove} variant="contained" color="success"
            disabled={!confirmValue.trim()}>
            Aprobar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const thSx = { fontWeight: 700, fontSize: 12, color: "#333", textTransform: "uppercase", letterSpacing: 0.4 } as const;
